import { Blocs } from '@business/blocs';
import { UserMessage } from '@business/entities/account';
import { GlobalNotification } from '@business/entities/common';
import { JobResponse } from '@business/entities/job';
import { FlashMessage } from '@core/components/FlashMessage';
import { BaseBloc } from '@core/utils/bloc';
import { Echo } from '@core/utils/echo/echo';
import { gcLabel } from '@core/utils/hooks/misc/gcLabel';
import { resolve } from '@core/utils/ioc';
import { R } from '@core/utils/r';
import { Session } from '@modules/auth/session';
import { openOrganizationOverlay } from '@modules/organizations/OrganizationOverlay';
import { Subject } from 'rxjs';
import { EstimateData, HotnessData, MessageKind, TrackerData } from './../../entities/account/UserMessage';

export class EchoBloc extends BaseBloc {
  private echo: Echo | null = null;

  onInit() {
    this.echo = new Echo();
    this.subscribeToUserChannel();
    this.subscribeToTenantChannel();
  }

  onReset() {
    this.echo?.disconnect();
  }

  subscribe<T = any>(channelName: string, event?: string) {
    const obs$ = new Subject<T>();
    this.echo?.subscribe<T>(channelName, event, data => {
      obs$.next(data);
    });
    return obs$;
  }

  unsubscribe(channelName: string) {
    this.echo?.leave(channelName);
  }

  handleUserMessage(message: UserMessage) {
    switch (message.kind) {
      case MessageKind.clockIn: {
        resolve(Blocs.clockin).getActive().subscribe();
        break;
      }
      case MessageKind.tracker: {
        const data = message.data as TrackerData;
        resolve(Blocs.timeLog).fetchSingleTimelog(data.id).subscribe();
        break;
      }
      case MessageKind.tasks: {
        resolve(Blocs.task).fetchTasks().subscribe();
        resolve(Blocs.todoChecklists).fetchChecklists().subscribe();
        break;
      }
      case MessageKind.jobs: {
        resolve(Blocs.job).fetchJobs().subscribe();
        if (message.data && R.get(message, 'data.success')) {
          resolve(Blocs.job).handleJobCompletion(message.data as JobResponse);
        }
        break;
      }
      case MessageKind.hotness: {
        const data = message.data as HotnessData;

        FlashMessage.info(data.name, `${data.name} hotness changed from ${data.old} to ${data.new}`, {
          buttons: [FlashMessage.button('View', 'info', () => openOrganizationOverlay({ orgId: data.id }))],
          autoHide: true,
          duration: 5000,
          progressBar: true,
          layout: 'bottomRight',
        });
        break;
      }
      case MessageKind.estimate: {
        const data = message.data as EstimateData;
        if (!document.URL.includes('/estimate/view')) {
          FlashMessage.info(`${gcLabel('Estimate')} Open`, data.message, {
            autoHide: true,
            duration: 5000,
            progressBar: true,
            layout: 'bottomRight',
          });
        }
        break;
      }
    }
  }

  subscribeToUserChannel() {
    if (Session.user) {
      this.echo?.subscribe<UserMessage>(`App.Models.User.${Session.user.id}`, undefined, data => {
        this.handleUserMessage(data);
      });
    }
  }

  subscribeToTenantChannel() {
    this.echo?.subscribe<GlobalNotification>(`Succeed`, undefined, data => {
      if (data.type === 'App\\Notifications\\AppUpdateNotification') {
        FlashMessage.info(
          '<div class="d-flex align-items-center justify-content-center"><img style="width: 35px; margin-right: 10px;" src="/assets/images/favicon.png"><span class="font-weight-medium">Succeed is ready to update!</span></div>',
          '',
          {
            buttons: [FlashMessage.button('Refresh page', 'info', () => document.location.reload())],
            autoHide: false,
            duration: 10000,
            buttonClass: 'btn-sm',
            html: true,
            progressBar: false,
            layout: 'bottomRight',
          },
        );
      }
    });
  }
}
